import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PostermywallNavigationLogo} from '@Components/postermywall-navigation-logo';
import {redirectToPage} from '@Libraries/login-signup-library';
import {getAssetUrl} from '@Utils/s3.util';
import {noop} from '@Utils/general.util';
import {PromoCarouselSlide} from './components/promo-carousel-slide/promo-carousel-slide';
import {PromoCarouselPagination} from './components/promo-carousel-pagination/promo-carousel-pagination';
import {useIsDocumentHidden, usePrefersReducedMotion, usePromoCarouselAutoAdvance} from './login-signup-promo-carousel.hooks';
import type {LoginSignupPromoCarouselProps} from './login-signup-promo-carousel.types';
import {FIRST_SLIDE_INDEX, LOGIN_PAGE_LOGO_ASSET_NAME, PROMO_CAROUSEL_SLIDES} from './login-signup-promo-carousel.types';
import styles from './login-signup-promo-carousel.module.scss';

/**
 * The purple left panel of the login page and the reset-password screens (Figma `4553:40775`): the three AI promos
 * crossfading in turn, with a pagination capsule to jump between them.
 */
export function LoginSignupPromoCarousel({doesReferrerExist}: LoginSignupPromoCarouselProps): ReactElement {
  const [activeSlideIndex, setActiveSlideIndex] = useState(FIRST_SLIDE_INDEX);
  const [hasInitialArtworkSettled, setHasInitialArtworkSettled] = useState(false);
  const [isPointerOver, setIsPointerOver] = useState(false);
  const [hasFocusInside, setHasFocusInside] = useState(false);
  const isDocumentHidden = useIsDocumentHidden();
  const prefersReducedMotion = usePrefersReducedMotion();

  const advanceToNextSlide = useCallback((): void => {
    setActiveSlideIndex((currentSlideIndex) => {
      return (currentSlideIndex + 1) % PROMO_CAROUSEL_SLIDES.length;
    });
  }, []);

  usePromoCarouselAutoAdvance({
    activeSlideIndex,
    isPaused: isPointerOver || hasFocusInside || isDocumentHidden || prefersReducedMotion,
    advanceToNextSlide,
  });

  const onInitialArtworkSettled = useCallback((): void => {
    setHasInitialArtworkSettled(true);
  }, []);

  const shouldRequestSlideImage = (slideIndex: number): boolean => {
    return slideIndex === FIRST_SLIDE_INDEX || hasInitialArtworkSettled;
  };

  const isBackIconVisible = (): boolean => {
    return doesReferrerExist && !window.PMW.util.doesUrlContainEmbeddedEditorAuthenticationLayoverUri(window.location.href);
  };

  const getBackIcon = (): ReactElement | null => {
    if (!isBackIconVisible()) {
      return null;
    }
    return (
      <div className={styles.backIconContainer}>
        <Icon
          icon="icon-arrow-left"
          type={IconType.DARK_GHOST}
          shape={IconShape.SQUARE}
          size={IconSize.SIZE_ICON_20}
          className={styles.backIcon}
          onClick={(): void => {
            redirectToPage(window.PMW.util.homepage_url());
          }}
        />
      </div>
    );
  };

  const getLogoClasses = (): string => {
    return isBackIconVisible() ? `${styles.logo} ${styles.logoAfterBackIcon}` : styles.logo;
  };

  return (
    <div
      className={styles.promoCarousel}
      onBlur={(): void => {
        setHasFocusInside(false);
      }}
      onFocus={(): void => {
        setHasFocusInside(true);
      }}
      onMouseEnter={(): void => {
        setIsPointerOver(true);
      }}
      onMouseLeave={(): void => {
        setIsPointerOver(false);
      }}
    >
      {getBackIcon()}
      <PostermywallNavigationLogo imageUrl={getAssetUrl(LOGIN_PAGE_LOGO_ASSET_NAME)} containerClassName={getLogoClasses()} imageClassName={styles.logoImage} />

      {PROMO_CAROUSEL_SLIDES.map((slideContent, slideIndex) => {
        return (
          <PromoCarouselSlide
            content={slideContent}
            isActive={slideIndex === activeSlideIndex}
            key={slideContent.headingFirstLineLangKey}
            onImageSettled={slideIndex === FIRST_SLIDE_INDEX ? onInitialArtworkSettled : noop}
            shouldRequestImage={shouldRequestSlideImage(slideIndex)}
          />
        );
      })}

      <div className={styles.paginationContainer}>
        <PromoCarouselPagination activeSlideIndex={activeSlideIndex} onSlideSelect={setActiveSlideIndex} slideCount={PROMO_CAROUSEL_SLIDES.length} />
      </div>
    </div>
  );
}
